import type { Workbook, Worksheet } from 'exceljs';
import moment from 'moment-timezone';

import { type DateRange, type DurationTimeInMillis, type Meters, metersToKilometers, type TimestampInMillis } from '@cobliteam/common-types';

import { ActivityCompletionStatus, type Driver, NewActivityType, type NewRouteReport, type ReportActivity, RouteStatus } from '@cobliteam/api-lib';
import type { VehicleDto } from '@cobliteam/api-lib/src/__generated__/graphql';

import type { RoutingSpreadsheetNoPermissionMessage } from '../permissions';

const HEADER_ROW_WITH_DATE_INFO = 1;
const FIRST_ROW_AFTER_TEMPLATE_HEADER = 4;

export class RoutesSpreadsheetReportBuilder {
  public buildReport = async (spreadsheetTemplate: Blob, dateRange: DateRange, routesReport: NewRouteReport[]) => {
    const buffer = await this.readFile(spreadsheetTemplate);

    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();

    const file: Workbook = await workbook.xlsx.load(buffer);
    const sheet: any = file.getWorksheet(1);

    this.insertReportDate(sheet, dateRange);

    const sortedRoutesReport = routesReport.sort((a, b) => (b?.startTime?.planned || 0) - (a?.startTime?.planned || 0));

    sortedRoutesReport.map(
      (row, index) =>
        (sheet.insertRow(FIRST_ROW_AFTER_TEMPLATE_HEADER + index, this.rowTransformer(row)).alignment = {
          vertical: 'middle',
          horizontal: 'center',
        })
    );

    sheet.eachRow(
      { includeEmpty: true },
      (row: any) =>
        (row.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        })
    );

    const excelBuffer = await workbook.xlsx.writeBuffer();
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
  };

  private formatDate = (date?: TimestampInMillis) => (date ? moment(date).format('DD/MM/YYYY') : '-');

  private formatHeaderDate = (dateRange: DateRange) => {
    const { start, end } = dateRange;

    const startDate = this.formatDate(start);
    const endDate = this.formatDate(end);
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  };

  private translateStatus = (status: RouteStatus) => {
    switch (status) {
      case RouteStatus.DELAYED:
        return 'ATRASADA';
      case RouteStatus.EXECUTED:
        return 'REALIZADA';
      case RouteStatus.IN_PROGRESS:
        return 'EM ANDAMENTO';
      case RouteStatus.PLANNED:
        return 'PENDENTE';
      case RouteStatus.UNEXECUTED:
        return 'NÃO REALIZADA';
      case RouteStatus.PARTIALLY_EXECUTED:
        return 'EXECUTADA EM PARTE';
    }
  };

  private getDateHour = (date?: TimestampInMillis) => (date ? moment(date).format('HH:mm:ss') : '-');

  private insertReportDate = (worksheet: Worksheet, dateRange: DateRange) => {
    const headerValue = worksheet.getRow(HEADER_ROW_WITH_DATE_INFO).getCell(1);
    worksheet.getRow(1).getCell(1).value = `${headerValue} • ${this.formatHeaderDate(dateRange)}`;
  };

  private formatVehicleInfo = (vehicle?: VehicleDto) => {
    if (vehicle) {
      return vehicle.licensePlate ? vehicle.licensePlate : 'Veículo não identificado';
    }

    return 'Nenhum veículo alocado';
  };

  private formatDriverInfo = (driver?: Driver) => {
    return driver ? driver.name : 'Nenhum motorista associado';
  };

  private formatDuration = (durationInMilliseconds?: DurationTimeInMillis) => {
    if (durationInMilliseconds) {
      const momentDuration = moment.duration(durationInMilliseconds);

      const hours = Math.floor(momentDuration.asHours());
      const hoursString = hours > 0 ? `${hours} ${hours > 1 ? 'horas' : 'hora'}` : '';

      const minutes = Math.floor(momentDuration.minutes());
      const minutesString = `${minutes > 9 ? minutes : `0${minutes}`} ${minutes > 1 ? 'minutos' : 'minuto'}`;

      return `${hoursString} ${minutesString}`;
    }

    return '-';
  };

  private formatDistance = (distance?: Meters | RoutingSpreadsheetNoPermissionMessage) => {
    if (typeof distance === 'string') return distance;
    return distance ? metersToKilometers(distance).toFixed(2) : '-';
  };

  private serviceCounter = (activities: ReportActivity[]) => ({
    completed: activities.filter((activity) => activity.completionStatus === ActivityCompletionStatus.COMPLETED).length,
    notCompleted: activities.filter((activity) => activity.completionStatus !== ActivityCompletionStatus.COMPLETED).length,
  });

  private rowTransformer = (route: NewRouteReport) => {
    const { startTime, status, driver, name, vehicle, activities } = route;
    const stopActivities = activities.filter((activity) => activity.type === NewActivityType.STOP);
    const services = this.serviceCounter(stopActivities);

    return [
      startTime?.planned ? this.formatDate(startTime.planned) : '',
      name,
      this.formatVehicleInfo(vehicle),
      this.formatDriverInfo(driver),
      startTime?.planned ? this.getDateHour(startTime.planned) : '',
      startTime?.planned ? this.getDateHour(startTime.executed) : '',
      status ? this.translateStatus(status) : '',
      services.completed,
      services.notCompleted,
    ];
  };

  private readFile = (fileRes: Blob): Promise<Buffer> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(fileRes);
      reader.onload = () => {
        resolve(reader.result as Buffer);
      };
    });
}
